'use client';

/**
 * TranscriptRail — the right-hand (desktop) / lower (mobile) live-captions
 * surface of the v3 CallScreen (design turn 11a part A: layout + the
 * `pinnedSlot` seam; part B wires `<PinnedCard />` into it from
 * call-screen.tsx).
 *
 * [Turn 17] Only ever mounted POST-DOCK now (call-screen.tsx's pre-dock
 * layout renders neither this nor a top bar at all — see that file's Turn
 * 17 note) — by the time this renders, the call is always `live` and a
 * connected timestamp always exists.
 *
 * Reads directly from ChatContext rather than taking props (except
 * `connectedAt`, below): `callStartMessageIndexRef` + `messages` together
 * define exactly "this call's" transcript — everything sent/said since the
 * call started, INCLUDING Sara's current in-flight turn: voice streaming
 * upserts straight into `messages` via appendAssistantDelta (the same
 * pipeline text mode uses — the old separate LiveUtterance model, FB22, is
 * gone), so there is no second "live line" to render or dedup against. The
 * selection itself lives in call-transcript.ts's `selectCallWindowMessages`
 * — shared with orb-docking.ts's `hasCallContent` (FB35) so the two can't
 * disagree about what "this call's" content is. A streaming message is just
 * a `Message` with `streaming: true`; `TranscriptBubble`'s `live` prop is
 * what turns on its caret.
 *
 * Mirrors conversation-area.tsx's use-stick-to-bottom usage for the
 * scrollable region — same auto-scroll-to-latest behavior, deliberately NOT
 * sharing ConversationArea's scroll-up history pagination (a call transcript
 * has no "older pages" to load).
 *
 * `pinnedSlot` renders BETWEEN the header and the `showCC` branch — i.e.
 * outside the captions-gated region — on purpose: a pinned card must stay
 * visible even when the user has turned captions off (the "Captions are off"
 * hint below it is muted decoration, not a reason to hide the pin).
 */

import { useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { StickToBottom, useStickToBottomContext } from 'use-stick-to-bottom';
import { useChatContext } from '@/contexts/ChatContext';
import { MessageRole } from '@/types/chat';
import { TranscriptBubble } from './transcript-bubble';
import { selectCallWindowMessages } from '@/lib/call-transcript';
import { formatConnectedClock } from '@/lib/call-clock';
import { cn } from '@/lib/utils';

/**
 * Explicit auto-follow for the rail. The library's own ResizeObserver-driven
 * follow proved unreliable for this instance (observed: content grows,
 * scrollTop stays 0 — likely its isAtBottom tracking going stale on a surface
 * that mounts empty inside an animated overlay), so every transcript change
 * requests the bottom imperatively. `escapedFromLock` is the lib's
 * user-scrolled-away signal — a user reading older turns is never yanked
 * back down; returning to the bottom re-engages following. `streamingLength`
 * replaces the old `liveUtterance.text` length (FB22) as the "content is
 * still growing" signal — the streaming message now lives in `messages`
 * itself, so its content length is read straight off the last turn message.
 */
function FollowLatest({
  turnCount,
  streamingLength,
}: {
  turnCount: number;
  streamingLength: number;
}) {
  const { scrollToBottom, escapedFromLock } = useStickToBottomContext();
  useEffect(() => {
    if (!escapedFromLock) scrollToBottom();
  }, [turnCount, streamingLength, escapedFromLock, scrollToBottom]);
  return null;
}

export interface TranscriptRailProps {
  className?: string;
  /** Pinned-card slot, rendered between the header and the scrollable
   *  transcript (see file header for why it sits outside the `showCC`
   *  gate). Wired from call-screen.tsx as `<PinnedCard />` (design turn
   *  11a part B). */
  pinnedSlot?: ReactNode;
  /** [Turn 17] Wall-clock epoch ms captured when the call went `live`
   *  (call-screen.tsx's `liveAtRef`) — backs the "Connected · HH:MM" text:
   *  desktop's rail-header (17d), mobile's centered pill above the first
   *  message (17a). Always provided in practice — this component only ever
   *  mounts post-dock, by which point the call has necessarily been live
   *  for a while (see file header) — but stays optional so a caller can't
   *  crash on a theoretical gap; falls back to "now" rather than rendering
   *  a broken/blank time. */
  connectedAt?: number;
  /** [Turn 17, 17d] The Live pill, rendered inline at the right end of the
   *  desktop header row (after "Connected · HH:MM"). The mock floats it at
   *  the SURFACE's top-right — but the mock's transcript panel is inset
   *  from the surface edge, while this rail is flush to it, so a floated
   *  pill would overlap this header's own right-aligned text (verified
   *  live). Inline placement keeps both readable in one row. call-screen
   *  owns the node (its `showCC`-driven visibility included). */
  headerAccessory?: ReactNode;
}

export function TranscriptRail({
  className,
  pinnedSlot,
  connectedAt,
  headerAccessory,
}: TranscriptRailProps) {
  const { messages, showCC, callStartMessageIndexRef } = useChatContext();

  // "This call's" transcript — see call-transcript.ts's own doc comment;
  // shared with orb-docking.ts's hasCallContent so the two selections can
  // never drift on what counts as "this call's" content (FB35).
  const turnMessages = useMemo(
    () => selectCallWindowMessages(messages, callStartMessageIndexRef.current),
    [messages, callStartMessageIndexRef]
  );

  const connectedLabel = useMemo(
    () => `Connected · ${formatConnectedClock(connectedAt ?? Date.now())}`,
    [connectedAt]
  );

  // Auto-follow signal while the last turn is mid-stream — see
  // FollowLatest's declaration comment.
  const streamingTailLength = useMemo(() => {
    const last = turnMessages[turnMessages.length - 1];
    return last?.streaming ? (last.content?.length ?? 0) : 0;
  }, [turnMessages]);

  return (
    <div
      className={cn(
        // min-h-0/min-w-0 are the rail's own containment contract: it is
        // only ever mounted as a flex-1 child, and without them its
        // min-content size (a tall/wide pinned card) inflates the parent —
        // on phones that pushed the call controls below the overlay clip
        // (FB26). Owning them here means no call site can forget one.
        // pt below sm stands in for the hidden header row (see below) so
        // the pinned card / first bubble doesn't touch the top of the
        // overlay. [Turn 17, 17a] `bg-white/95` (the mobile "card" look) is
        // gone — the rail is transparent on mobile now, bubbles float
        // directly on CallScreen's gradient; desktop's translucent+blur
        // chrome is unchanged.
        'flex h-full min-h-0 min-w-0 flex-col pt-[12px] sm:pt-0',
        'sm:bg-[rgba(255,255,255,.66)] sm:backdrop-blur-[12px]',
        'sm:border-l sm:border-[#f0e2cd]',
        className
      )}
    >
      {/* Header — desktop only (FB27 #3): on phones the docked top header
          (call-top-bar-status.tsx) already shows the Live pill, and
          "Transcript" labels the only thing on screen — the row just cost
          vertical space. [Turn 17, 17d] The Live pill itself moved OUT of
          this header entirely — CallScreen now renders it absolute, top-
          right of the whole overlay (matching the mock's placement once the
          transcript panel opens) — so this row is just the two plain text
          labels; the "Connected · HH:MM" side stays mounted unconditionally
          (nothing here toggles on `showCC` anymore), so the header's height
          never changes on any state change — that stability is what used to
          require the invisible-not-unmounted Live pill trick. */}
      <div className="hidden shrink-0 items-center justify-between px-6 pb-3.5 pt-[18px] sm:flex">
        <span className="font-['Work_Sans'] text-[15px] font-semibold text-[#31364E]">
          Transcript
        </span>
        <div className="flex items-center gap-[12px]">
          <span className="text-[11.5px] text-[#b3a996]">{connectedLabel}</span>
          {headerAccessory}
        </div>
      </div>

      {/* Pinned-card slot — see file header for why this sits outside the
          showCC gate below (must stay visible with captions off). */}
      {pinnedSlot}

      {showCC ? (
        <StickToBottom
          className="relative min-h-0 flex-1 overflow-y-hidden"
          initial="smooth"
          resize="smooth"
          role="log"
        >
          <StickToBottom.Content className="flex flex-col gap-3.5 px-6 pb-[20px]">
            <FollowLatest
              turnCount={turnMessages.length}
              streamingLength={streamingTailLength}
            />
            {/* [Turn 17, 17a] Mobile-only "Connected · HH:MM" pill — the
                desktop equivalent lives in the header above (`connectedLabel`
                on the right of "Transcript"); on mobile there's no header
                row at all, so this sits as the first item in the scrollable
                list itself, floating on the transparent rail's gradient. */}
            <div className="flex justify-center sm:hidden">
              <span className="rounded-[20px] bg-[rgba(255,255,255,.6)] px-[10px] py-[3px] text-[10px] text-[#b3a996]">
                {connectedLabel}
              </span>
            </div>
            {turnMessages.map((m) => (
              <TranscriptBubble
                key={m.id}
                role={m.role === MessageRole.USER ? 'user' : 'assistant'}
                live={!!m.streaming}
              >
                {m.content}
              </TranscriptBubble>
            ))}
          </StickToBottom.Content>
        </StickToBottom>
      ) : (
        // Captions off — the rail chrome (header, surface) stays; only the
        // transcript content is replaced with a muted hint.
        <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 pb-[20px] text-center">
          <span className="text-[13px] font-bold text-[#b3a996]">CC</span>
          <span className="text-[13px] text-[#b3a996]">Captions are off</span>
        </div>
      )}
    </div>
  );
}
